import React from "react";
import {
  MapPin,
  Users,
  ExternalLink,
  Heart,
  Calendar,
  DollarSign,
  Briefcase,
} from "lucide-react";
import { JobPosting } from "../../types/job";

interface JobCardProps {
  job: JobPosting;
  onBookmark: (jobId: string) => void;
  viewMode?: "grid" | "list";
}

const JobCard: React.FC<JobCardProps> = ({
  job,
  onBookmark,
  viewMode = "grid",
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1일 전";
    if (diffDays < 7) return `${diffDays}일 전`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)}주 전`;
    return `${Math.ceil(diffDays / 30)}개월 전`;
  };

  const isDeadlineNear = () => {
    const deadline = new Date(job.deadline);
    const now = new Date();
    const diffTime = deadline.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays > 0;
  };

  const isDeadlinePassed = () => {
    const deadline = new Date(job.deadline);
    const now = new Date();
    return deadline.getTime() < now.getTime();
  };

  if (viewMode === "list") {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start gap-4">
          {/* 회사 로고 */}
          <img
            src={job.companyLogo}
            alt={job.company}
            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
          />

          {/* 메인 콘텐츠 */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {job.title}
                </h3>
                <p className="text-gray-600 font-medium">{job.company}</p>
              </div>
              <button
                onClick={() => onBookmark(job.id)}
                className={`p-2 rounded-full transition-colors ${
                  job.isBookmarked
                    ? "text-red-500 bg-red-50 hover:bg-red-100"
                    : "text-gray-400 hover:text-red-500 hover:bg-red-50"
                }`}
              >
                <Heart
                  className={`w-5 h-5 ${job.isBookmarked ? "fill-current" : ""}`}
                />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{job.location}</span>
              </div>
              <div className="flex items-center gap-1">
                <Briefcase className="w-4 h-4" />
                <span>{job.type}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{job.experience}</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                <span>{job.salary}</span>
              </div>
            </div>

            <p className="text-gray-700 text-sm mb-4 line-clamp-2">
              {job.description}
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              {job.requirements.slice(0, 4).map((req, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                >
                  {req}
                </span>
              ))}
              {job.requirements.length > 4 && (
                <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                  +{job.requirements.length - 4}개
                </span>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(job.postedAt)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{job.applicants}명 지원</span>
                </div>
                {isDeadlineNear() && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                    마감 임박
                  </span>
                )}
                {isDeadlinePassed() && (
                  <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                    마감됨
                  </span>
                )}
              </div>

              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                지원하기
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow relative">
      {/* 북마크 버튼 */}
      <button
        onClick={() => onBookmark(job.id)}
        className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${
          job.isBookmarked
            ? "text-red-500 bg-red-50 hover:bg-red-100"
            : "text-gray-400 hover:text-red-500 hover:bg-red-50"
        }`}
      >
        <Heart
          className={`w-5 h-5 ${job.isBookmarked ? "fill-current" : ""}`}
        />
      </button>

      {/* 추천 배지 */}
      {job.isRecommended && (
        <div className="absolute -top-2 -left-2 z-10">
          <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
            <span>추천</span>
          </div>
        </div>
      )}

      {/* 회사 로고 */}
      <div className="flex items-center gap-3 mb-4">
        <img
          src={job.companyLogo}
          alt={job.company}
          className="w-12 h-12 rounded-lg object-cover"
        />
        <div>
          <h3 className="font-semibold text-gray-900">{job.title}</h3>
          <p className="text-gray-600 text-sm">{job.company}</p>
        </div>
      </div>

      {/* 기본 정보 */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin className="w-4 h-4" />
          <span>{job.location}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Briefcase className="w-4 h-4" />
          <span>
            {job.type} · {job.experience}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <DollarSign className="w-4 h-4" />
          <span>{job.salary}</span>
        </div>
      </div>

      {/* 설명 */}
      <p className="text-gray-700 text-sm mb-4 line-clamp-3">
        {job.description}
      </p>

      {/* 기술 스택 */}
      <div className="flex flex-wrap gap-1 mb-4">
        {job.requirements.slice(0, 3).map((req, index) => (
          <span
            key={index}
            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
          >
            {req}
          </span>
        ))}
        {job.requirements.length > 3 && (
          <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
            +{job.requirements.length - 3}
          </span>
        )}
      </div>

      {/* 하단 정보 */}
      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(job.postedAt)}</span>
        </div>
        <div className="flex items-center gap-1">
          <Users className="w-4 h-4" />
          <span>{job.applicants}명</span>
        </div>
      </div>

      {/* 마감일 알림 */}
      {(isDeadlineNear() || isDeadlinePassed()) && (
        <div className="mb-4">
          {isDeadlineNear() && (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
              마감 임박
            </span>
          )}
          {isDeadlinePassed() && (
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
              마감됨
            </span>
          )}
        </div>
      )}

      {/* 액션 버튼 */}
      <div className="flex gap-2">
        <button className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
          지원하기
        </button>
        <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default JobCard;
